import clsx from "clsx";
import type { FaClientListItem } from "@/services/api/agentic-v2";
import { ClientAvatar } from "./ClientAvatar";
import { formatRelativeTime } from "@/components/missions/MissionRow";
import { Badge } from "@/components/ui/badge/Badge";
import { Button } from "@/components/ui/button/Button";
import { StatusDot } from "@/components/ui/status-dot";
import { isHouseholdClient } from "./clientDisplay";
import {
  compactJourneyStageLabel,
  journeyStageBadgeColor,
  journeyStageRowAccentClass,
  journeyStageStatusDotColor,
  shortJourneyStageLabel,
} from "./clientStageDisplay";

/**
 * One row in the Clients list — designed for scanability.
 *
 *   [avatar]  {name} · {members count}                {last activity}
 *             {journey stage}
 *             {insight summary — up to 2 lines}
 *             {N missions}  {N open tasks}
 */
export interface ClientRowProps {
  client: FaClientListItem;
  onClick: (client: FaClientListItem) => void;
}

export function ClientRow({ client, onClick }: ClientRowProps) {
  const household = isHouseholdClient(client.type, client.member_count);
  const insight = client.ai_insight;
  const journeyCompact = compactJourneyStageLabel(client.journey_stage);
  const journeyFull = shortJourneyStageLabel(client.journey_stage);
  const hasJourney = Boolean(journeyCompact);

  return (
    <Button
      type="button"
      plain
      size="unstyled"
      onClick={() => onClick(client)}
      data-testid={`client-row-${client._id}`}
      data-focus-ref={`client:${client._id}`}
      className={clsx(
        "flex w-full items-start gap-3 rounded-lg px-3 py-3 text-left transition-colors",
        hasJourney && "border-l-2",
        hasJourney && journeyStageRowAccentClass(client.journey_stage),
        "hover:bg-slate-100 focus:bg-slate-100 focus:outline-none dark:hover:bg-slate-800/40 dark:focus:bg-slate-800/40",
        "focus-visible:ring-1 focus-visible:ring-indigo-400/40",
      )}
    >
      <ClientAvatar name={client.display_name} size="sm" />

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-3">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
              {client.display_name}
            </span>
            {household && (client.member_count ?? 0) > 0 && (
              <span className="text-xs text-slate-500 shrink-0">
                · {client.member_count} members
              </span>
            )}
          </div>
          <span className="text-xs text-slate-500 tabular-nums whitespace-nowrap">
            {client.last_activity_at
              ? formatRelativeTime(client.last_activity_at)
              : "—"}
          </span>
        </div>

        {journeyCompact ? (
          <div
            className="mt-1 flex items-center gap-1.5 min-w-0"
            title={journeyFull ?? undefined}
          >
            <StatusDot
              color={journeyStageStatusDotColor(client.journey_stage)}
              size="sm"
            />
            <Badge color={journeyStageBadgeColor(client.journey_stage)} size="sm">
              {journeyCompact}
            </Badge>
          </div>
        ) : client.plannr_stage_display ? (
          <div className="mt-1">
            <Badge color="zinc" size="sm">
              {client.plannr_stage_display}
            </Badge>
          </div>
        ) : null}

        {insight?.summary && (
          <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-slate-600 dark:text-slate-400">
            {insight.summary}
          </p>
        )}

        {(client.active_mission_count > 0 || client.open_task_count > 0) && (
          <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500">
            {client.active_mission_count > 0 && (
              <Chip tone="emerald">
                {client.active_mission_count} mission
                {client.active_mission_count === 1 ? "" : "s"}
              </Chip>
            )}
            {client.open_task_count > 0 && (
              <Chip tone="amber">
                {client.open_task_count} open task
                {client.open_task_count === 1 ? "" : "s"}
              </Chip>
            )}
          </div>
        )}
      </div>
    </Button>
  );
}

function Chip({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: "emerald" | "amber";
}) {
  const cls =
    tone === "emerald"
      ? "text-emerald-700 dark:text-emerald-300/90"
      : "text-amber-700 dark:text-amber-300/90";
  return <span className={cls}>{children}</span>;
}
